import React from 'react';
import { Link } from 'react-router-dom';
import SharedLayout from '@/components/SharedLayout';
import SubpageNavigation from '@/components/SubpageNavigation';
import SEOHead from '@/components/SEOHead';
import { CANONICAL_URLS } from '@/utils/canonicalUrls';

const AdhdPomodoroTimer = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ADHD Pomodoro Timer",
    "applicationCategory": "ProductivityApplication",
    "operatingSystem": "Web Browser",
    "url": CANONICAL_URLS.adhdPomodoroTimer,
    "description":
      "A free ADHD-friendly Pomodoro timer with short, manageable focus intervals, gentle audio cues, and a calm visual interface designed to reduce overwhelm.",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5",
    },
  };

  return (
    <>
      <SEOHead
        title="ADHD Pomodoro Timer — Free Focus Timer for ADHD Brains 2026"
        description="A free Pomodoro timer designed for ADHD focus. Short, manageable intervals, calm visuals, and gentle alerts to reduce overwhelm and beat task paralysis."
        keywords="adhd pomodoro timer, adhd timer, adhd focus timer, pomodoro for adhd, adhd study timer, body doubling timer"
        canonicalUrl={CANONICAL_URLS.adhdPomodoroTimer}
        schemaData={schema}
      />
      <SharedLayout>
        <article className="text-left px-6 py-12 max-w-4xl mx-auto">
          <SubpageNavigation />

          <header>
            <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-800 dark:text-gray-100">
              ADHD Pomodoro Timer — Free Focus Timer Built for ADHD Brains
            </h1>
            <p className="text-xl mb-6 text-gray-700 dark:text-gray-200">
              The Pomodoro Technique is one of the most ADHD-friendly productivity methods ever made. Short,
              clearly-bounded focus intervals turn an overwhelming task into something your brain can actually start.
              This <strong>free ADHD Pomodoro timer</strong> is designed around that — calm visuals, gentle audio,
              fully customizable.
            </p>
          </header>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Why Pomodoro Works for ADHD</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-200">
              <li className="mb-2"><strong>Beats task paralysis</strong> — "just 25 minutes" is far easier to start than "do the project"</li>
              <li className="mb-2"><strong>External structure</strong> — the timer holds time so your brain doesn't have to</li>
              <li className="mb-2"><strong>Built-in dopamine</strong> — finishing a Pomodoro is a tiny, frequent win</li>
              <li className="mb-2"><strong>Forced breaks</strong> — protects against hyperfocus burnout</li>
              <li className="mb-2"><strong>Works for body doubling</strong> — pair with a study-with-me stream and go</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">ADHD-Friendly Settings That Actually Help</h2>
            <p className="mb-4 text-gray-700 dark:text-gray-200">
              The classic 25/5 cycle is a great default, but ADHD brains are not one-size-fits-all. Try these:
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700 dark:text-gray-200">
              <li className="mb-2"><strong>Hard-to-start days:</strong> 10 minutes on / 5 minutes off — momentum first, length later</li>
              <li className="mb-2"><strong>Hyperfocus days:</strong> 50 minutes on / 10 minutes off — ride the wave but force the break</li>
              <li className="mb-2"><strong>Reading or admin:</strong> 15 minutes on / 3 minutes off — short bursts beat marathons</li>
              <li className="mb-2"><strong>Use a calm background</strong> — visual chaos feeds distractibility; pick a soft Unsplash scene</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-100">Tips for Sticking With It</h2>
            <ol className="list-decimal pl-6 text-gray-700 dark:text-gray-200">
              <li className="mb-2"><strong>Pick one task</strong> before you start the timer — decision fatigue kills sessions</li>
              <li className="mb-2"><strong>Capture distractions on paper</strong> — write the thought down, return to the task</li>
              <li className="mb-2"><strong>Reward yourself</strong> — small wins after every Pomodoro keeps dopamine flowing</li>
              <li className="mb-2"><strong>Don't aim for perfect</strong> — one finished Pomodoro beats a flawless plan you didn't start</li>
            </ol>
            <div className="mt-8">
              <Link
                to="/"
                className="inline-block bg-primary text-primary-foreground font-medium py-3 px-6 rounded-lg shadow-sm hover:opacity-90 transition"
              >
                Start the Timer →
              </Link>
              <Link
                to="/aesthetic-pomodoro-timer"
                className="inline-block ml-3 underline text-gray-700 dark:text-gray-200"
              >
                Prefer the aesthetic version?
              </Link>
            </div>
          </section>
        </article>
      </SharedLayout>
    </>
  );
};

export default AdhdPomodoroTimer;
